import { CALENDAR_PX_PER_MIN } from '@/utils/calendar/event-layout'
import { computed, onMounted, onUnmounted, ref } from 'vue'

/** Short UTC offset label for the user's local timezone (e.g. GMT+2 during CEST). */
export const formatLocalTimezoneOffset = (date: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'shortOffset' }).formatToParts(
    date
  )
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
}

/**
 * Current-time line position for the day/week time grid, refreshed every minute.
 */
export function useCalendarNowIndicator() {
  const now = ref(new Date())
  let intervalId: ReturnType<typeof setInterval> | undefined

  onMounted(() => {
    now.value = new Date()
    intervalId = setInterval(() => {
      now.value = new Date()
    }, 60_000)
  })

  onUnmounted(() => {
    if (intervalId !== undefined) {
      clearInterval(intervalId)
    }
  })

  const nowIndicatorTopPx = computed(
    () => (now.value.getHours() * 60 + now.value.getMinutes()) * CALENDAR_PX_PER_MIN
  )

  const timezoneLabel = computed(() => formatLocalTimezoneOffset(now.value))

  return {
    now,
    nowIndicatorTopPx,
    timezoneLabel
  }
}

/** Whether the now-line should render for the visible day or week. */
export const isNowVisibleOnTimeGrid = (
  now: Date,
  activeView: 'day' | 'week',
  selectedDate: Date,
  weekDayDates: Date[]
): boolean => {
  const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  if (activeView === 'day') {
    return sameDay(selectedDate, now)
  }

  return weekDayDates.some((day) => sameDay(day, now))
}
